<template>
  <div class="sidebar-inner">
    <!-- Top section -->
    <div class="top-section">
      <h4 v-if="!isLoadingIncidentReport" class="mb-1">Incident Report</h4>
      <h4 v-if="isLoadingIncidentReport" class="mb-1">Loading...</h4>
      <hr>
      <!-- Is Loading -->
      <div class="text-center mt-4" v-if="isLoadingIncidentReport">
        <b-spinner class="m-2" variant="itg-dark" role="status"></b-spinner>
      </div>
      <!-- / Is Loading -->

      <!-- No Incident Report -->
      <div v-if="!isLoadingIncidentReport && !incidentReport" class="no-incident-report">
        <b class="text-danger mb-3 d-block">No incident report</b>

        <a href="#" @click.prevent="openIncidentReportModal" class="btn btn-itg-dark btn-block">
          <i class="fa fa-plus mr-1"></i>
          Add Report
        </a>
      </div>
      <!-- / No Incident Report -->

      <!-- Has Incident Report -->
      <div v-if="!isLoadingIncidentReport && incidentReport">

        <div class="property-card mb-3">
          <div class="property-name">
            Last Updated
          </div>
          <div class="property-value">
            {{ displayTimeAgo(incidentReport.updated_at) }}
          </div>
        </div>

        <!-- Status Card - Always show -->
        <div class="property-card mb-3">
          <div class="property-name">
            Status
          </div>
          <div class="property-value">
            <span v-if="incidentReport.closed_at" class="badge badge-danger">Closed</span>
            <span v-else class="badge badge-success">Open</span>
          </div>
        </div>

        <!-- Assigned to department -->
        <div class="property-card mb-3">
          <div class="property-name">
            Assigned to Department
          </div>
          <div class="property-value">
            <span v-if="incidentReport.assigned_to_department == 'cs'">CX</span>
            <span v-else-if="incidentReport.assigned_to_department == 'sales'">Sales</span>
            <span v-else>{{ incidentReport.assigned_to_department }}</span>
          </div>
        </div>

        <!-- ITG Included Activity - Show if it has a value -->
        <div v-if="incidentReport.itg_included_activity !== null && incidentReport.itg_included_activity !== undefined" class="property-card mb-3">
          <div class="property-name">
            ITG Included Activity
          </div>
          <div class="property-value">
            <span v-if="incidentReport.itg_included_activity === true" class="badge badge-success">Yes</span>
            <span v-else-if="incidentReport.itg_included_activity === false" class="badge badge-secondary">No</span>
            <span v-else>-</span>
          </div>
        </div>

        <!-- Closed At Card - Show if incident report is closed -->
        <div v-if="incidentReport.closed_at" class="property-card mb-3">
          <div class="property-name">
            Closed At
          </div>
          <div class="property-value">
            {{ displayTimeAgo(incidentReport.closed_at) }}
          </div>
        </div>

        <div class="d-flex">
          <a v-if="!incidentReport.closed_at" href="#" @click.prevent="openIncidentReportModal" class="btn btn-itg-dark flex-fill mr-2">
            <i class="fa fa-edit mr-1"></i>
            Edit
          </a>

          <a href="#" @click.prevent="openIncidentReportModalReadOnly" class="btn btn-itg-dark flex-fill">
            <i class="fa fa-eye mr-1"></i>
            View
          </a>
        </div>

        <!-- Assign To Department Button -->
        <a href="#" @click.prevent="openAssignDepartmentModal" class="btn btn-itg-dark btn-block mt-2">
          <i class="fa fa-building mr-1"></i>
          Assign To Department
        </a>

        <!-- Share Externally Button - Show if incident report exists -->
        <a href="#" @click.prevent="openShareExternallyModal" class="btn btn-itg-dark btn-block mt-2">
          <i class="fa fa-share mr-1"></i>
          Share Externally ({{ sharedUsersCount }})
        </a>

        <!-- Close Incident Button - Show if incident report is not closed -->
        <a v-if="!incidentReport.closed_at" href="#" @click.prevent="closeIncidentReport" class="btn btn-itg-dark btn-block mt-2">
          <i class="fa fa-check-circle mr-1"></i>
          Close Incident
        </a>

        <!-- Reopen Incident Button - Show if incident report is closed -->
        <a v-if="incidentReport.closed_at" href="#" @click.prevent="reopenIncidentReport" class="btn btn-itg-dark btn-block mt-2">
          <i class="fa fa-undo mr-1"></i>
          Reopen Incident
        </a>
      </div>
      <!-- / Has Incident Report -->

    </div>
    <!-- / Top section -->
    <!-- Bottom section -->
    <div v-if="!isLoadingIncidentReport && incidentReport" class="bottom-section pt-3 pb-3">
      <a href="#" @click.prevent="downloadPdf" class="btn btn-itg-dark btn-block">
        <i class="fa fa-file-pdf mr-1"></i>
        Download PDF
      </a>
    </div>
    <!-- / Bottom section -->

    <!-- Incident Report Modal -->
    <incident-report-modal 
      ref="incidentReportModal"
      @incident-report-saved="onIncidentReportSaved"
    />
    <!-- / Incident Report Modal -->

    <!-- Share Externally Modal -->
    <share-externally-modal 
      ref="shareExternallyModal"
    />
    <!-- / Share Externally Modal -->

    <!-- Assign Department Modal -->
    <b-modal
      id="assign-department-modal"
      title="Assign To Department"
      centered
      @hide="closeAssignDepartmentModal"
      :no-close-on-backdrop="false"
      :no-close-on-esc="false"
    >
      <div class="form-group">
        <label for="department">Department</label>
        <select
          id="department"
          v-model="selectedDepartment"
          class="form-control"
        >
          <option value="">Select department</option>
          <option value="cs">CS</option>
          <option value="sales">Sales</option>
        </select>
      </div>

      <template #modal-footer>
        <div class="w-100 d-flex justify-content-end">
          <button 
            type="button" 
            class="btn btn-itg mr-2" 
            @click="closeAssignDepartmentModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-itg-dark"
            @click="assignDepartment"
            :disabled="!selectedDepartment"
          >
            Assign
          </button>
        </div>
      </template>
    </b-modal>
    <!-- / Assign Department Modal -->
  </div>
</template>

<script>
import Forms from "../../../../../mixins/Forms";
import Dates from "../../../../../mixins/Dates";
import Tickets from "../../../../../mixins/Tickets";
import {mapGetters, mapActions} from "vuex";
import IncidentReportModal from "./IncidentReportSidebar/IncidentReportModal.vue";
import ShareExternallyModal from "./IncidentReportSidebar/ShareExternallyModal.vue";
import axios from 'axios';

export default {
  name: "IncidentReportSidebar",
  mixins: [Forms, Tickets, Dates],
  components: {
    IncidentReportModal,
    ShareExternallyModal
  },
  data() {
    return {
      selectedDepartment: ''
    }
  },
  created() {
    this.loadIncidentReport();
    this.loadSharedUsers();
  },
  computed: {
    ...mapGetters('ticketViewer', {
      ticket: 'ticket',
      incidentReport: 'incidentReport',
      isLoadingIncidentReport: 'isLoadingIncidentReport',
      sharedUsers: 'sharedUsers',
      isLoadingSharedUsers: 'isLoadingSharedUsers'
    }),

    sharedUsersCount() {
      return this.sharedUsers ? this.sharedUsers.length : 0;
    }
  },
  methods: {
    ...mapActions('ticketViewer', {
      loadIncidentReport: 'loadIncidentReport',
      loadSharedUsers: 'loadSharedUsers'
    }),

    openIncidentReportModal() {
      this.$refs.incidentReportModal.openModal();
    },

    openIncidentReportModalReadOnly() {
      this.$refs.incidentReportModal.openModal(true);
    },

    openShareExternallyModal() {
      this.$refs.shareExternallyModal.openModal();
    },

    openAssignDepartmentModal() {
      this.selectedDepartment = this.incidentReport.assigned_to_department || '';
      this.$bvModal.show('assign-department-modal');
    },

    closeAssignDepartmentModal() {
      this.$bvModal.hide('assign-department-modal');
      this.selectedDepartment = '';
    },

    assignDepartment() {
      if (!this.selectedDepartment) {
        this.$bvToast.toast('Please select a department', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        return;
      }

      if (!this.ticket || !this.ticket.id) {
        this.$bvToast.toast('Ticket information not available', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        return;
      }

      const ticketId = this.ticket.id;
      
      // Show loading state
      // this.$bvToast.toast('Assigning to department...', {
      //   title: 'Info',
      //   variant: 'info',
      //   solid: true
      // });

      axios.put(API_BASE + `/tickets/${ticketId}/incident-report`, {
        assigned_to_department: this.selectedDepartment
      }).then(response => {
        // Reload the incident report data
        this.loadIncidentReport();
        
        // Close modal
        this.closeAssignDepartmentModal();
        
        // Show success message
        this.$bvToast.toast('Department assigned successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      }).catch(error => {
        console.error('Error assigning department:', error);
        this.$bvToast.toast('Failed to assign department', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      });
    },

    onIncidentReportSaved(incidentReportData) {
      // Reload the incident report data
      this.loadIncidentReport();
      this.loadSharedUsers();
      
      // Show success message
      // this.$bvToast.toast('Incident report saved successfully', {
      //   title: 'Success',
      //   variant: 'success',
      //   solid: true
      // });
    },

    closeIncidentReport() {
      swal({
        title: "Close Incident Report?",
        text: "Are you sure you want to close this incident report?.",
        icon: "warning",
        buttons: {
          cancel: {
            text: "Cancel",
            value: false,
            visible: true,
            // className: "btn btn-secondary",
            closeModal: true,
          },
          confirm: {
            text: "Yes, Close Incident",
            value: true,
            visible: true,
            // className: "btn btn-danger",
            closeModal: true,
          }
        },
        dangerMode: true,
      }).then((willClose) => {
        if (willClose) {
          this.performCloseIncidentReport();
        }
      });
    },

    performCloseIncidentReport() {
      if (!this.ticket || !this.ticket.id) {
        this.$bvToast.toast('Ticket information not available', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        return;
      }

      const ticketId = this.ticket.id;
      
      // Show loading state
      this.$bvToast.toast('Closing incident report...', {
        title: 'Info',
        variant: 'info',
        solid: true
      });

      axios.post(API_BASE + `/tickets/${ticketId}/incident-report/close`).then(response => {
        // Reload the incident report data
        this.loadIncidentReport();
        this.loadSharedUsers();
        
        // Show success message
        this.$bvToast.toast('Incident report closed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      }).catch(error => {
        console.error('Error closing incident report:', error);
        this.$bvToast.toast('Failed to close incident report', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      });
    },

    reopenIncidentReport() {
      swal({
        title: "Reopen Incident Report?",
        text: "Are you sure you want to reopen this incident report?",
        icon: "warning",
        buttons: {
          cancel: {
            text: "Cancel",
            value: false,
            visible: true,
            // className: "btn btn-secondary",
            closeModal: true,
          },
          confirm: {
            text: "Yes, Reopen Incident",
            value: true,
            visible: true,
            // className: "btn btn-danger",
            closeModal: true,
          }
        },
        dangerMode: true,
      }).then((willReopen) => {
        if (willReopen) {
          this.performReopenIncidentReport();
        }
      });
    },

    performReopenIncidentReport() {
      if (!this.ticket || !this.ticket.id) {
        this.$bvToast.toast('Ticket information not available', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        return;
      }

      const ticketId = this.ticket.id;
      
      // Show loading state
      this.$bvToast.toast('Reopening incident report...', {
        title: 'Info',
        variant: 'info',
        solid: true
      });

      axios.post(API_BASE + `/tickets/${ticketId}/incident-report/reopen`).then(response => {
        // Reload the incident report data
        this.loadIncidentReport();
        this.loadSharedUsers();
        
        // Show success message
        this.$bvToast.toast('Incident report reopened successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      }).catch(error => {
        console.error('Error reopening incident report:', error);
        this.$bvToast.toast('Failed to reopen incident report', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      });
    },

    downloadPdf() {
      if (!this.ticket || !this.ticket.id) {
        this.$bvToast.toast('Ticket information not available', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        return;
      }

      const ticketId = this.ticket.id;
      
      // Show loading state
      this.$bvToast.toast('Downloading PDF...', {
        title: 'Info',
        variant: 'info',
        solid: true
      });

      axios.get(API_BASE + `/tickets/${ticketId}/incident-report/download-pdf`, {
        responseType: 'blob',
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `incident_report_ticket_${ticketId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        // Clean up the URL object
        window.URL.revokeObjectURL(url);
        
        // Show success message
        this.$bvToast.toast('PDF downloaded successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      }).catch(error => {
        console.error('Error downloading PDF:', error);
        this.$bvToast.toast('Failed to download PDF', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      });
    }
  }
}
</script>

<style scoped lang="scss">
.no-incident-report {
  background: #fff;
  border-radius: 5px;
  padding: 15px;

  b {
    font-weight: 600;
  }
}

.has-incident-report {
  background: #fff;
  border-radius: 5px;
  padding: 15px;

  .alert {
    margin-bottom: 15px;
  }
}

.property-card {
    display: block;
    background: #fff;
    border-radius: 5px;
    padding: 15px;

    .property-name {
        font-weight: 600;
        color: #2c5cc5;
    }

    .property-value {
        font-size: 12px;
      line-height: 19px;
    }
}
</style>
